import { EmptyState } from '@/components/custom/empty-state';
import { Filter, FilterActions, FilterContent, FilterRadioGroup, FilterSelectNative } from '@/components/custom/filter';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { DUEL_NOTIFICATION_CLOSED_EVENT } from '@/lib/duel-notification-events';
import { DuelsCard } from '../duels-card';
import { Requests } from '../duels-requests';
import { useWeeklyChallenge } from '@/hooks/useWeeklyChallenge';
import { DuelsNew } from '../duels-new';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { DuelsPlayer } from '../duels-player';
import { Loader } from '@/components/custom/loader';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import * as Yup from 'yup';
import { Button } from '@/components/ui/button';
import { DashboardContent } from '@/layouts/dashboard';
import { CardWrapper } from '@/components/ui/card';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';

export default function DuelsDefault(props) {
  const { uri } = props;
  const [newDuelModal, setNewDuelModal] = useState(false);
  const [openPlayerModal, setOpenPlayerModal] = useState(false);
  const [duel, setDuel] = useState(false);
  const [taxonomies, setTaxonomies] = useState([]);
  const [duels, setDuels] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [loading, setLoading] = useState();
  const [loadingReceivedRequests, setLoadingReceivedRequests] = useState();
  const [loadingSentRequests, setLoadingSentRequests] = useState();
  const [winner, setWinner] = useState();
  const [activeReceivedTab, setActiveReceivedTab] = useState('active');
  const [selectedOpponent, setSelectedOpponent] = useState({});

  const client = useClient();

  const { setCurrentChallenge, setCurrentStep, setCurrentRound, setChallengeType } = useWeeklyChallenge();

  const filters = useFormik({
    initialValues: {
      topic: 'blank',
      status: 'ongoing'
    }
  });

  const getTaxonomies = async () => {
    try {
      const data = await client.fetch('/duels/taxonomies_for_creation');
      setTaxonomies(data);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorGetTaxonomy'));
    }
  };

  const getSentRequests = async () => {
    setLoadingSentRequests(true);
    const { data, error } = await client.fetch('/duels/sent');
    if (error) {
      toast.error(t('toast.errorGetDuelsSentRequests'));
    } else {
      setSentRequests(data);
    }
    setLoadingSentRequests(false);
  };

  const getReceivedRequests = async () => {
    setLoadingReceivedRequests(true);
    const { data, error } = await client.fetch(`/duels/received?status=${activeReceivedTab}`);
    if (error) {
      toast.error(t('toast.errorGetDuelsReceivedRequests'));
    } else {
      setReceivedRequests(data);
    }
    setLoadingReceivedRequests(false);
  };

  const getDuels = async () => {
    let url = `/duels?status=${filters.values.status}`;
    if (filters.values.topic !== 'blank') {
      url;
    }

    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error(t('toast.errorGetDuels'));
    } else {
      setDuels(data);
    }
  };

  useEffect(() => {
    getReceivedRequests();
  }, [activeReceivedTab]);

  const handleStart = async duel => {
    setCurrentStep(1);
    setDuel(duel);
    setChallengeType('duel');
    setCurrentChallenge(duel);
    setCurrentRound(duel?.['rounds-completed-by-me'] + 1);
    setOpenPlayerModal(true);
  };

  const getWinner = async id => {
    const { data, error } = await client.fetch(`/duels/${id}/winner`);

    if (error) {
      toast.error('Erro ao buscar vencedor');
    } else {
      setWinner(data);
    }
  };

  const showResults = duel => {
    setCurrentChallenge(duel);
    getWinner(duel?.id);
    setOpenPlayerModal(true);
    setCurrentStep(3);
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([getTaxonomies(), getDuels(), getSentRequests(), getReceivedRequests()]).finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    getDuels();
  }, [filters.values]);

  useEffect(() => {
    if (!openPlayerModal) {
      getDuels();
    }
  }, [openPlayerModal]);

  const updateDuelsAndRequests = async () => {
    await Promise.all([getDuels(), getReceivedRequests()]);
  };

  // O toast de notificação de duelo vive fora dessa árvore (montado no layout).
  // Se essa página estiver aberta quando ele fechar — aceitar, recusar, ignorar
  // ou fechar no X —, atualiza a listagem. O ref evita re-inscrever o listener
  // a cada render e mantém sempre a versão mais recente de updateDuelsAndRequests.
  const updateDuelsAndRequestsRef = useRef(updateDuelsAndRequests);
  updateDuelsAndRequestsRef.current = updateDuelsAndRequests;

  useEffect(() => {
    const handleDuelNotificationClosed = () => updateDuelsAndRequestsRef.current();
    window.addEventListener(DUEL_NOTIFICATION_CLOSED_EVENT, handleDuelNotificationClosed);
    return () => window.removeEventListener(DUEL_NOTIFICATION_CLOSED_EVENT, handleDuelNotificationClosed);
  }, []);

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: uri,
        name: 'Duelos',
        isActive: true
      }
    ]
  };

  const filteredDuels = duels.filter(duel => filters.values.topic === 'blank' || duel?.['parent-taxonomy']?.id === Number(filters.values.topic));

  const createDuel = async newDuel => {
    const { error } = await client.mutate('duels', { invited_id: newDuel['invited-id'], parent_taxonomy_id: Number(newDuel.topic) });
    if (error) {
      const errorMessage = error.message || t('toast.errorCreateDuel');
      toast.error(errorMessage);
    } else {
      getSentRequests();
      setNewDuelModal(false);
      toast.success(t('toast.successCreatedDuel'));
    }
  };

  const schema = Yup.object({
    topic: Yup.string().required('Campo obrigatório'),
    'invited-id': Yup.string().required('Campo obrigatório')
  });

  const duelForm = useFormik({
    initialValues: {
      topic: '',
      'invited-id': null
    },
    validationSchema: schema,
    onSubmit: values => {
      createDuel(values);
    }
  });

  const handleDialogClose = () => {
    setNewDuelModal(false);
    setSelectedOpponent({});
    duelForm.resetForm();
  };

  const statusRadios = [
    { id: 'ongoing', value: 'ongoing', label: 'Em andamento' },
    { id: 'expired', value: 'expired', label: 'Expirados' },
    { id: 'completed', value: 'completed', label: 'Finalizados' }
  ];

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      <Filter
        variant="plain"
        className="mb-11"
      >
        <FilterContent>
          <FilterSelectNative
            blankLabel={t('filter.blankLabelAll2')}
            label={t('filter.selectTopic')}
            value={filters.values.topic}
            onChange={e => filters.setFieldValue('topic', e.target.value)}
            options={taxonomies}
          />
          <FilterRadioGroup
            value={filters.values.status}
            onValueChange={value => filters.setFieldValue('status', value)}
            radios={statusRadios}
          />
        </FilterContent>

        <FilterActions>
          <Dialog
            open={newDuelModal}
            onOpenChange={newOpenState => {
              setNewDuelModal(newOpenState);
              // Só execute a função se o modal estiver fechando
              if (!newOpenState) {
                handleDialogClose();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button wide>Novo duelo</Button>
            </DialogTrigger>

            <DuelsNew
              open={newDuelModal}
              taxonomies={taxonomies}
              selectedOpponent={selectedOpponent}
              setSelectedOpponent={setSelectedOpponent}
              duelForm={duelForm}
            />
          </Dialog>
        </FilterActions>
      </Filter>

      {loading && <Loader />}

      {!loading && (
        <Page>
          <PageContent>
            {filteredDuels.length > 0 && (
              <CardWrapper size="md">
                {filteredDuels.map(duel => (
                  <DuelsCard
                    key={duel.id}
                    handleStart={handleStart}
                    showResults={showResults}
                    duel={duel}
                    status={filters?.values?.status}
                  />
                ))}
              </CardWrapper>
            )}

            {filteredDuels.length === 0 && (
              <EmptyState
                type="select"
                title="Crie um duelo"
                text="Inicie uma novo duelo e convide um oponente para participar"
              />
            )}
          </PageContent>

          <PageSidebar variant="md">
            <Requests
              type="received"
              data={receivedRequests}
              setActiveTab={setActiveReceivedTab}
              activeTab={activeReceivedTab}
              updateDuelsAndRequests={updateDuelsAndRequests}
              loadingReceived={loadingReceivedRequests}
            />
            <Requests
              type="sent"
              data={sentRequests}
              loadingSent={loadingSentRequests}
            />
          </PageSidebar>
        </Page>
      )}

      <DuelsPlayer
        setOpenPlayerModal={setOpenPlayerModal}
        setNewDuelModal={setNewDuelModal}
        duel={duel}
        openPlayerModal={openPlayerModal}
        winner={winner}
        getWinner={getWinner}
        setWinner={setWinner}
        onCloseRefresh={updateDuelsAndRequests}
      />
    </DashboardContent>
  );
}
